import React, { useEffect, useState } from "react";
import { connect } from "react-redux";
import ReactSelect from "react-select";
import makeAnimated from "react-select/animated";
import { useField, useFormikContext } from "formik";

import { FormLabel } from "../TextInput";
import { Error } from "../Error";
import { theme } from "../../../theme";
import { getDocumentSubCategories } from "../../../actions/companyActions";
import { useCurrentCompanyValue } from "hooks/auth";

const animatedComponents = makeAnimated();

export const DocumentSubCategoryComponent = ({
	name,
	options,
	setFieldValue,
	defaultValue,
	label,
	required,
	values,
	getDocumentSubCategories,
	selectedCategory,
	disabled,
	lockedSubcategoryId,
	...props
}) => {
	const profileTypeId = useCurrentCompanyValue("profile_type")?.id;
	//eslint-disable-next-line
	const [field, meta] = useField(name);
	const { setFieldTouched } = useFormikContext();
	const [optionsArray, setOptionsArray] = useState([]);
	const [value, setValueState] = useState(null);
	const selected = optionsArray.find(
		(o) => o.value.id === values?.[name]?.id ?? values?.[name]
	) || null;

	useEffect(() => {
		getDocumentSubCategories();
	}, [getDocumentSubCategories]);

	useEffect(() => {
		if (!optionsArray.length) {
			return;
		}

		const currentValue = values?.[name];

		// Case 1: already hydrated → do nothing
		if (currentValue && typeof currentValue === "object" && currentValue.id) {
			return;
		}

		// Case 2: initial ID → hydrate it
		if (typeof currentValue === "number") {
			const matched = optionsArray.find(
				(o) => o.value.id === currentValue
			);

			if (matched) {
				setFieldValue(name, matched.value);
			}
			return;
		}

		// Case 3: empty → auto-select
		if (!currentValue) {
			let selectedOption = null;

			if (lockedSubcategoryId) {
				selectedOption =
					optionsArray.find(
						(o) => o.value.id === lockedSubcategoryId
					) ?? optionsArray[0];
			} else {
				selectedOption = optionsArray[0];
			}

			if (selectedOption) {
				setFieldValue(name, selectedOption.value);
			}
		}
	}, [optionsArray, lockedSubcategoryId, name, setFieldValue, values]);

	useEffect(() => {
		let _optionsArray = [];

		if (options.length && selectedCategory) {
			_optionsArray = options
				.filter((o) => o.parent_id === selectedCategory)
				.filter((o) =>
					selectedCategory === 3
						? o.profile_types.includes(profileTypeId)
						: true
				)
				.map((option) => ({
					value: { id: option.id, name: option.name },
					label: option.name,
				}));
		}

		setOptionsArray(_optionsArray);
	}, [selectedCategory, options, profileTypeId]);

	const customStyles = {
		option: (provided, state) => ({
			...provided,
			cursor: "pointer",
			color: state.isSelected ? "#ffffff" : theme.colors.brandGray,
		}),
		control: (provided) => ({
			...provided,
			padding: "0.338rem",
			minHeight: 0,
			borderColor: "#e7eaee",
			marginBottom: "1.25rem",
			color: "green",
		}),
		singleValue: (provided) => ({
			...provided,
			color: theme.colors.brandGray,
		}),
	};

	const handleOnChange = (option) => {
		setFieldValue(name, option ? option.value : "");
	};

	return (
		<div
			className={props.className}
			data-testid={"document-sub-category"}
		>
			<FormLabel>
				Sub Category
				{required && <span>*</span>}
			</FormLabel>

			<ReactSelect
				name={name}
				styles={customStyles}
				closeMenuOnSelect={true}
				components={{ animatedComponents, IndicatorSeparator: () => null }}
				onChange={handleOnChange}
				value={selected}
				onBlur={() => setFieldTouched(name, true)}
				options={optionsArray}
				isDisabled={disabled}
			/>

			<Error meta={meta} />
		</div>
	);
};

const mapStateToProps = (state) => {
	return {
		options: state.companyProfile.documentSubCategories,
	};
};

export const DocumentSubCategory = connect(mapStateToProps, {
	getDocumentSubCategories,
})(DocumentSubCategoryComponent);
